import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from "react";

import type { Fixture, Product, Shelf } from "../types";
import {
  DEFAULT_RACK_HEIGHT,
  KIND_COLOR,
  MAX_RACK_HEIGHT,
  PRODUCT_COLORS,
  SHELF_THICKNESS,
  placementIssues,
  productWidth,
  rackSize,
  shelfClearance,
  shelfFill,
  shelvesByElevation,
} from "../lib/planogram";
import { ui } from "../lib/ui";

/** Shelf design for one fixture: its rack drawn as a front elevation at real
 *  scale — the floor at the bottom, shelf boards at their heights, product
 *  facings standing on them. Everything is vector config in metres (the UI
 *  talks centimetres); nothing here touches footage or footfall data.
 *
 *  Drag a board up or down to move a shelf; drag a product run along a shelf
 *  or onto another one. Placements that do not fit (overlapping, past the
 *  rack's end, taller than the gap above) are flagged, never blocked — the
 *  point is to experiment. */

type Point = [number, number]; // [x along the front, elevation] in metres

interface ShelfEditorProps {
  fixture: Fixture;
  onChange: (fixture: Fixture) => void;
  onBack: () => void;
  /** Products already placed anywhere in this plan, for one-click re-adding. */
  catalogue?: Product[];
}

type Selection =
  | { type: "shelf"; id: string }
  | { type: "product"; shelfId: string; id: string }
  | null;

type Drag =
  | { mode: "shelf"; id: string; offset: number }
  | { mode: "product"; shelfId: string; id: string; offset: Point };

// Canvas margins around the rack, in metres (elevation labels on the left,
// the floor scale underneath).
const ML = 0.4;
const MR = 0.35;
const MT = 0.15;
const MB = 0.25;
const MAX_CANVAS_HEIGHT = 560; // px — a tall narrow fridge must not fill the page
const MIN_RACK_HEIGHT = 0.2;
const MIN_PRODUCT = 0.01; // metres
const NEW_SHELF_STEP = 0.35; // metres above the current top shelf
const SHELF_HIT = 0.04; // metres of extra grab area around a board

const LABEL = "pointer-events-none select-none fill-text font-sans";

const mm = (v: number) => Math.round(v * 1000) / 1000; // store at mm precision
const cm = (v: number) => Math.round(v * 100) / 100; // drag snap: whole centimetres
const clamp = (v: number, lo: number, hi: number) => Math.min(Math.max(v, lo), hi);
const toCm = (m: number) => Math.round(m * 1000) / 10;
const pct = (f: number) => `${Math.round(f * 100)}%`;

function nextId(prefix: string, taken: { id: string }[]): string {
  const ids = new Set(taken.map((t) => t.id));
  let n = taken.length + 1;
  while (ids.has(`${prefix}${n}`)) n += 1;
  return `${prefix}${n}`;
}

export function ShelfEditor({ fixture, onChange, onBack, catalogue = [] }: ShelfEditorProps) {
  const [selection, setSelection] = useState<Selection>(null);
  const [drag, setDrag] = useState<Drag | null>(null);
  // Screen pixels per metre; the canvas is sized explicitly so the SVG never
  // letterboxes and screen↔metre conversion stays a plain scale.
  const [scale, setScale] = useState(200);

  const wrapRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  const { front, depth } = rackSize(fixture);
  const H = fixture.height ?? DEFAULT_RACK_HEIGHT;
  const shelves = shelvesByElevation(fixture.shelves);
  const vw = front + ML + MR;
  const vh = H + MT + MB;
  const k = 1 / scale; // metres per screen pixel — constant-size text/handles

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const update = () => {
      const w = el.getBoundingClientRect().width;
      if (w > 0) setScale(Math.min(w / vw, MAX_CANVAS_HEIGHT / vh));
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [vw, vh]);

  const selectedShelf =
    selection?.type === "shelf"
      ? shelves.find((s) => s.id === selection.id)
      : selection?.type === "product"
        ? shelves.find((s) => s.id === selection.shelfId)
        : undefined;
  const selectedProduct =
    selection?.type === "product"
      ? selectedShelf?.products.find((p) => p.id === selection.id)
      : undefined;

  // --- model helpers ---------------------------------------------------------

  /** Every edit pins the height: a rack with shelves always knows how tall
   *  it is, even if the operator never touched the default. */
  const patch = (changes: Partial<Fixture>) =>
    onChange({ ...fixture, height: H, ...changes });

  const patchShelf = (id: string, changes: Partial<Shelf>) =>
    patch({ shelves: fixture.shelves.map((s) => (s.id === id ? { ...s, ...changes } : s)) });

  const patchProduct = (shelfId: string, id: string, changes: Partial<Product>) =>
    patch({
      shelves: fixture.shelves.map((s) =>
        s.id === shelfId
          ? { ...s, products: s.products.map((p) => (p.id === id ? { ...p, ...changes } : p)) }
          : s,
      ),
    });

  const allProducts = fixture.shelves.flatMap((s) => s.products);

  /** Move a product run onto another shelf (keeping its x). Ids only have to
   *  be unique per shelf, so a run whose id is taken on the target shelf gets
   *  a fresh one; returns the id it ends up with. */
  const moveProduct = (fromId: string, toId: string, id: string, x: number): string => {
    const target = fixture.shelves.find((s) => s.id === toId);
    const product = fixture.shelves.find((s) => s.id === fromId)?.products.find((p) => p.id === id);
    if (!target || !product) return id;
    const newId = target.products.some((p) => p.id === id) ? nextId("p", allProducts) : id;
    patch({
      shelves: fixture.shelves.map((s) => {
        if (s.id === fromId) return { ...s, products: s.products.filter((p) => p.id !== id) };
        if (s.id === toId) {
          return { ...s, products: [...s.products, { ...product, id: newId, x }] };
        }
        return s;
      }),
    });
    setSelection({ type: "product", shelfId: toId, id: newId });
    return newId;
  };

  const addShelf = () => {
    const id = nextId("s", fixture.shelves);
    let elevation: number;
    if (shelves.length === 0) {
      elevation = Math.min(0.1, H - SHELF_THICKNESS);
    } else {
      const top = shelves[shelves.length - 1].elevation;
      if (top + NEW_SHELF_STEP <= H - 0.1) {
        elevation = top + NEW_SHELF_STEP;
      } else {
        // No room above: split the widest gap instead.
        const levels = [0, ...shelves.map((s) => s.elevation), H];
        let best = 0;
        let at = H / 2;
        for (let i = 1; i < levels.length; i += 1) {
          const gap = levels[i] - levels[i - 1];
          if (gap > best) {
            best = gap;
            at = (levels[i] + levels[i - 1]) / 2;
          }
        }
        elevation = at;
      }
    }
    patch({ shelves: [...fixture.shelves, { id, elevation: cm(elevation), products: [] }] });
    setSelection({ type: "shelf", id });
  };

  const addEvenShelves = (count: number) => {
    const bottom = Math.min(0.1, H / 4);
    const top = H - Math.min(0.3, H / 4);
    const step = count > 1 ? (top - bottom) / (count - 1) : 0;
    const fresh: Shelf[] = [];
    for (let i = 0; i < count; i += 1) {
      fresh.push({
        id: nextId("s", [...fixture.shelves, ...fresh]),
        elevation: cm(bottom + i * step),
        products: [],
      });
    }
    patch({ shelves: [...fixture.shelves, ...fresh] });
    setSelection(null);
  };

  /** Target shelf for a new product: the selected one, else the lowest. */
  const targetShelf = (): Shelf | undefined => selectedShelf ?? shelves[0];

  const addProduct = (template?: Product) => {
    const shelf = targetShelf();
    if (!shelf) return;
    const count = fixture.shelves.reduce((n, s) => n + s.products.length, 0);
    const base: Product = template
      ? { ...template }
      : {
          id: "",
          name: "",
          x: 0,
          w: 0.1,
          h: 0.2,
          facings: 1,
          color: PRODUCT_COLORS[count % PRODUCT_COLORS.length],
        };
    const id = nextId("p", allProducts);
    // Right after the last run on the shelf if that still fits, else from the
    // left end — the operator drags it where it belongs anyway.
    const end = shelf.products.reduce((max, p) => Math.max(max, p.x + productWidth(p)), 0);
    const x = end + productWidth(base) <= front + 1e-6 ? mm(end) : 0;
    patchShelf(shelf.id, { products: [...shelf.products, { ...base, id, x }] });
    setSelection({ type: "product", shelfId: shelf.id, id });
  };

  const duplicateProduct = () => {
    if (!selectedShelf || !selectedProduct) return;
    const id = nextId("p", allProducts);
    const x = mm(
      clamp(
        selectedProduct.x + productWidth(selectedProduct),
        0,
        Math.max(0, front - productWidth(selectedProduct)),
      ),
    );
    patchShelf(selectedShelf.id, {
      products: [...selectedShelf.products, { ...selectedProduct, id, x }],
    });
    setSelection({ type: "product", shelfId: selectedShelf.id, id });
  };

  const removeSelected = useCallback(() => {
    if (!selection) return;
    if (selection.type === "shelf") {
      onChange({
        ...fixture,
        height: H,
        shelves: fixture.shelves.filter((s) => s.id !== selection.id),
      });
    } else {
      onChange({
        ...fixture,
        height: H,
        shelves: fixture.shelves.map((s) =>
          s.id === selection.shelfId
            ? { ...s, products: s.products.filter((p) => p.id !== selection.id) }
            : s,
        ),
      });
    }
    setSelection(null);
  }, [selection, fixture, H, onChange]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (t && ["INPUT", "TEXTAREA", "SELECT"].includes(t.tagName)) return;
      if ((e.key === "Delete" || e.key === "Backspace") && selection) {
        e.preventDefault();
        removeSelected();
      }
      if (e.key === "Escape") setSelection(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selection, removeSelected]);

  // --- coordinates -----------------------------------------------------------

  /** SVG y for an elevation (the canvas is y-down, the rack is measured up). */
  const sy = (elevation: number) => H - elevation;

  /** Screen → [x along the front, elevation], metres, unclamped. */
  const toRack = useCallback(
    (clientX: number, clientY: number): Point | null => {
      const svg = svgRef.current;
      if (!svg) return null;
      const rect = svg.getBoundingClientRect();
      if (rect.width === 0) return null;
      const x = (clientX - rect.left) / scale - ML;
      const y = (clientY - rect.top) / scale - MT; // y-down from the rack top
      return [x, H - y];
    },
    [scale, H],
  );

  // --- pointer interaction ---------------------------------------------------

  const capture = (e: ReactPointerEvent<Element>) =>
    svgRef.current?.setPointerCapture(e.pointerId);

  const onShelfDown = (shelf: Shelf) => (e: ReactPointerEvent<SVGRectElement>) => {
    if (e.button !== 0) return;
    e.stopPropagation();
    const p = toRack(e.clientX, e.clientY);
    if (!p) return;
    capture(e);
    setSelection({ type: "shelf", id: shelf.id });
    setDrag({ mode: "shelf", id: shelf.id, offset: shelf.elevation - p[1] });
  };

  const onProductDown =
    (shelf: Shelf, product: Product) => (e: ReactPointerEvent<SVGGElement>) => {
      if (e.button !== 0) return;
      e.stopPropagation();
      const p = toRack(e.clientX, e.clientY);
      if (!p) return;
      capture(e);
      setSelection({ type: "product", shelfId: shelf.id, id: product.id });
      setDrag({
        mode: "product",
        shelfId: shelf.id,
        id: product.id,
        offset: [p[0] - product.x, p[1] - shelf.elevation],
      });
    };

  const onCanvasDown = (e: ReactPointerEvent<SVGSVGElement>) => {
    if (e.button !== 0) return;
    setSelection(null);
  };

  const onPointerMove = (e: ReactPointerEvent<SVGSVGElement>) => {
    if (!drag) return;
    const p = toRack(e.clientX, e.clientY);
    if (!p) return;

    if (drag.mode === "shelf") {
      const elevation = cm(clamp(p[1] + drag.offset, 0, H - SHELF_THICKNESS));
      const shelf = fixture.shelves.find((s) => s.id === drag.id);
      if (shelf && shelf.elevation !== elevation) patchShelf(drag.id, { elevation });
      return;
    }

    const from = fixture.shelves.find((s) => s.id === drag.shelfId);
    const product = from?.products.find((q) => q.id === drag.id);
    if (!from || !product) return;
    const x = cm(clamp(p[0] - drag.offset[0], 0, Math.max(0, front - productWidth(product))));
    // The run lands on the highest shelf its bottom edge is (nearly) above —
    // so dragging upwards hops it onto the next board.
    const bottom = p[1] - drag.offset[1] + 0.08;
    const target =
      [...shelves].reverse().find((s) => s.elevation <= bottom) ?? shelves[0] ?? from;
    if (target.id !== from.id) {
      const id = moveProduct(from.id, target.id, product.id, x);
      setDrag({ ...drag, shelfId: target.id, id });
    } else if (product.x !== x) {
      patchProduct(from.id, product.id, { x });
    }
  };

  const onPointerUp = (e: ReactPointerEvent<SVGSVGElement>) => {
    if (!drag) return;
    svgRef.current?.releasePointerCapture(e.pointerId);
    setDrag(null);
  };

  // --- render ----------------------------------------------------------------

  // Floor scale: a tick every 50 cm, every 25 cm on short racks.
  const tickStep = front <= 1.5 ? 0.25 : 0.5;
  const ticks: number[] = [];
  for (let x = 0; x <= front + 1e-6; x += tickStep) ticks.push(x);
  if (front - ticks[ticks.length - 1] > 0.05) ticks.push(front);

  const catalogueChips = catalogue.filter((p) => p.name.trim() !== "").slice(0, 12);
  const productCount = fixture.shelves.reduce((n, s) => n + s.products.length, 0);
  const issues =
    selectedShelf && selectedProduct
      ? placementIssues(selectedProduct, selectedShelf, shelves, front, H)
      : null;
  const issueText = issues
    ? [
        issues.overflow && "extends past the rack's end",
        issues.tooTall &&
          `taller than the ${toCm(shelfClearance(selectedShelf!, shelves, H))} cm gap above`,
        issues.overlaps.length > 0 &&
          `overlaps ${issues.overlaps
            .map((id) => selectedShelf!.products.find((p) => p.id === id)?.name || id)
            .join(", ")}`,
      ]
        .filter(Boolean)
        .join(" · ")
    : "";

  const kindColor = KIND_COLOR[fixture.kind];
  const cursor = drag ? "cursor-grabbing" : "cursor-default";

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" className={ui.btnQuiet} onClick={onBack}>
          ← Floor plan
        </button>
        <span className="text-[15px] font-semibold">
          <span
            className="mr-1.5 inline-block h-2.5 w-2.5 rounded-[3px] align-[-1px]"
            style={{ background: kindColor }}
          />
          {fixture.label || fixture.kind}
        </span>
        <span className="ml-auto flex items-center gap-2 text-xs text-muted">
          Front {front} m · Depth {depth} m · Height
          <input
            className={`${ui.input} w-[76px]`}
            type="number"
            min={MIN_RACK_HEIGHT * 100}
            max={MAX_RACK_HEIGHT * 100}
            step={5}
            value={toCm(H)}
            onChange={(e) =>
              patch({
                height: mm(
                  clamp(
                    (Number(e.target.value) || 0) / 100,
                    MIN_RACK_HEIGHT,
                    MAX_RACK_HEIGHT,
                  ),
                ),
              })
            }
            aria-label="Rack height (cm)"
          />
          cm
        </span>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <button type="button" className={ui.btnQuiet} onClick={addShelf}>
          + Shelf
        </button>
        <button
          type="button"
          className={ui.btnQuiet}
          onClick={() => addProduct()}
          disabled={shelves.length === 0}
          title={shelves.length === 0 ? "Add a shelf first" : undefined}
        >
          + Product
        </button>
        {shelves.length === 0 && (
          <button type="button" className={ui.btnQuiet} onClick={() => addEvenShelves(5)}>
            Start with 5 shelves
          </button>
        )}
        {catalogueChips.length > 0 && shelves.length > 0 && (
          <>
            <span className={`${ui.label} ml-2`}>Add again</span>
            {catalogueChips.map((p) => (
              <button
                key={`${p.name}|${p.w}|${p.h}`}
                type="button"
                className={ui.chip}
                onClick={() => addProduct(p)}
                title={`${toCm(p.w)} × ${toCm(p.h)} cm`}
              >
                <span
                  className="mr-1.5 inline-block h-2.5 w-2.5 rounded-[3px] align-[-1px]"
                  style={{ background: p.color }}
                />
                {p.name}
              </button>
            ))}
          </>
        )}
        <span className="ml-auto text-xs text-muted">
          {shelves.length} {shelves.length === 1 ? "shelf" : "shelves"} · {productCount}{" "}
          {productCount === 1 ? "product run" : "product runs"}
        </span>
      </div>

      <div
        ref={wrapRef}
        className="overflow-hidden rounded-card border border-border bg-surface leading-[0]"
      >
        <svg
          ref={svgRef}
          className={`mx-auto block touch-none ${cursor}`}
          style={{ width: vw * scale, height: vh * scale }}
          viewBox={`${-ML} ${-MT} ${vw} ${vh}`}
          onPointerDown={onCanvasDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
        >
          {/* back panel + frame */}
          <rect x={0} y={0} width={front} height={H} fill="var(--bg)" />
          <rect
            x={0}
            y={0}
            width={front}
            height={H}
            fill="none"
            stroke={kindColor}
            strokeWidth={2}
            vectorEffect="non-scaling-stroke"
            opacity={0.8}
          />

          {/* floor + scale */}
          <line
            x1={-ML / 2}
            y1={H}
            x2={front + MR / 2}
            y2={H}
            stroke="var(--text)"
            strokeWidth={3}
            vectorEffect="non-scaling-stroke"
            opacity={0.75}
          />
          {ticks.map((x) => (
            <g key={`tick-${x}`}>
              <line
                x1={x}
                y1={H}
                x2={x}
                y2={H + 5 * k}
                stroke="var(--muted)"
                strokeWidth={1}
                vectorEffect="non-scaling-stroke"
              />
              <text
                x={x}
                y={H + 16 * k}
                textAnchor="middle"
                className={`${LABEL} fill-muted!`}
                fontSize={10 * k}
              >
                {toCm(x)}
              </text>
            </g>
          ))}
          <text
            x={front / 2}
            y={H + 30 * k}
            textAnchor="middle"
            className={`${LABEL} fill-muted!`}
            fontSize={10 * k}
          >
            cm along the front
          </text>

          {/* shelves (boards + elevation labels + fill), lowest first */}
          {shelves.map((s) => {
            const selected = selection?.type === "shelf" && selection.id === s.id;
            const fill = shelfFill(s, front);
            return (
              <g key={s.id}>
                <rect
                  x={0}
                  y={sy(s.elevation)}
                  width={front}
                  height={SHELF_THICKNESS}
                  fill={selected ? "var(--accent)" : "var(--text)"}
                  opacity={selected ? 1 : 0.7}
                />
                {/* grab area, wider than the board */}
                <rect
                  x={-0.02}
                  y={sy(s.elevation) - SHELF_HIT}
                  width={front + 0.04}
                  height={SHELF_THICKNESS + 2 * SHELF_HIT}
                  fill="transparent"
                  className="cursor-grab"
                  onPointerDown={onShelfDown(s)}
                />
                <text
                  x={-0.06}
                  y={sy(s.elevation) + SHELF_THICKNESS / 2}
                  textAnchor="end"
                  dominantBaseline="central"
                  className={`${LABEL} ${selected ? "fill-accent!" : "fill-muted!"}`}
                  fontSize={10 * k}
                  fontWeight={selected ? 600 : 400}
                >
                  {toCm(s.elevation)} cm
                </text>
                <text
                  x={front + 0.06}
                  y={sy(s.elevation) + SHELF_THICKNESS / 2}
                  dominantBaseline="central"
                  className={`${LABEL} ${fill > 1 + 1e-6 ? "fill-danger!" : "fill-muted!"}`}
                  fontSize={10 * k}
                >
                  {pct(fill)}
                </text>
              </g>
            );
          })}

          {/* products, on top of their boards */}
          {shelves.map((s) =>
            s.products.map((p) => {
              const selected =
                selection?.type === "product" &&
                selection.shelfId === s.id &&
                selection.id === p.id;
              const width = productWidth(p);
              const iss = placementIssues(p, s, shelves, front, H);
              const flagged = iss.overflow || iss.tooTall || iss.overlaps.length > 0;
              const maxChars = Math.floor((width - 0.02) / (6.2 * k));
              const showText = maxChars >= 2 && p.h > 12 * k;
              const text =
                p.name.length > maxChars ? `${p.name.slice(0, Math.max(1, maxChars - 1))}…` : p.name;
              return (
                <g
                  key={`${s.id}-${p.id}`}
                  className="cursor-grab"
                  onPointerDown={onProductDown(s, p)}
                >
                  <rect
                    x={p.x}
                    y={sy(s.elevation + p.h)}
                    width={width}
                    height={p.h}
                    fill={p.color}
                    fillOpacity={selected ? 0.95 : 0.8}
                    stroke={selected ? "var(--accent)" : flagged ? "var(--danger)" : p.color}
                    strokeWidth={selected ? 2.5 : flagged ? 2 : 1}
                    strokeDasharray={flagged && !selected ? "4 3" : undefined}
                    vectorEffect="non-scaling-stroke"
                    rx={0.004}
                  />
                  {Array.from({ length: p.facings - 1 }, (_, i) => (
                    <line
                      key={i}
                      x1={p.x + (i + 1) * p.w}
                      y1={sy(s.elevation + p.h)}
                      x2={p.x + (i + 1) * p.w}
                      y2={sy(s.elevation)}
                      stroke="var(--bg)"
                      strokeWidth={1}
                      vectorEffect="non-scaling-stroke"
                      opacity={0.7}
                    />
                  ))}
                  {showText && (
                    <text
                      x={p.x + width / 2}
                      y={sy(s.elevation + p.h / 2)}
                      textAnchor="middle"
                      dominantBaseline="central"
                      className="pointer-events-none select-none font-sans"
                      fill="#fff"
                      fontSize={10 * k}
                      fontWeight={600}
                      style={{ paintOrder: "stroke" }}
                      stroke="rgba(0,0,0,0.35)"
                      strokeWidth={2 * k}
                    >
                      {text || "—"}
                    </text>
                  )}
                </g>
              );
            }),
          )}
        </svg>
      </div>

      <p className="text-xs text-muted">
        The rack seen from the aisle, at real scale. Drag a shelf board up or
        down to set its height; drag a product run along a shelf or onto
        another shelf. Runs that overlap, stick out past the end or are taller
        than the gap above are outlined in red — nothing is blocked, so try
        layouts freely. Delete removes the selected shelf or run. Changes are
        kept with the floor plan — save it when you are happy.
      </p>

      {selectedShelf && !selectedProduct && (
        <div className="flex flex-wrap items-end gap-3 rounded-card border border-border bg-surface px-4 py-3">
          <label className={ui.field}>
            Shelf height (cm from floor)
            <input
              className={`${ui.input} w-[96px]`}
              type="number"
              min={0}
              max={toCm(H - SHELF_THICKNESS)}
              step={1}
              value={toCm(selectedShelf.elevation)}
              onChange={(e) =>
                patchShelf(selectedShelf.id, {
                  elevation: mm(
                    clamp((Number(e.target.value) || 0) / 100, 0, H - SHELF_THICKNESS),
                  ),
                })
              }
            />
          </label>
          <span className="pb-2 text-xs text-muted">
            {selectedShelf.products.length}{" "}
            {selectedShelf.products.length === 1 ? "run" : "runs"} ·{" "}
            {pct(shelfFill(selectedShelf, front))} of the front used ·{" "}
            {toCm(shelfClearance(selectedShelf, shelves, H))} cm clear above
          </span>
          <button type="button" className={ui.btnQuiet} onClick={() => addProduct()}>
            + Product here
          </button>
          <button type="button" className={ui.btnDanger} onClick={removeSelected}>
            Remove shelf
          </button>
        </div>
      )}

      {selectedShelf && selectedProduct && (
        <div className="flex flex-col gap-2 rounded-card border border-border bg-surface px-4 py-3">
          <div className="flex flex-wrap items-end gap-3">
            <label className={ui.field}>
              Product
              <input
                className={`${ui.input} min-w-[180px]`}
                value={selectedProduct.name}
                placeholder="Name"
                autoFocus={selectedProduct.name === ""}
                onChange={(e) =>
                  patchProduct(selectedShelf.id, selectedProduct.id, { name: e.target.value })
                }
              />
            </label>
            {(
              [
                ["w", "Width (cm)", 0.5],
                ["h", "Height (cm)", 0.5],
              ] as const
            ).map(([field, label, step]) => (
              <label key={field} className={ui.field}>
                {label}
                <input
                  className={`${ui.input} w-[84px]`}
                  type="number"
                  min={MIN_PRODUCT * 100}
                  step={step}
                  value={toCm(selectedProduct[field])}
                  onChange={(e) =>
                    patchProduct(selectedShelf.id, selectedProduct.id, {
                      [field]: mm(Math.max(MIN_PRODUCT, (Number(e.target.value) || 0) / 100)),
                    })
                  }
                />
              </label>
            ))}
            <label className={ui.field}>
              Facings
              <input
                className={`${ui.input} w-[72px]`}
                type="number"
                min={1}
                max={200}
                step={1}
                value={selectedProduct.facings}
                onChange={(e) =>
                  patchProduct(selectedShelf.id, selectedProduct.id, {
                    facings: clamp(Math.round(Number(e.target.value) || 1), 1, 200),
                  })
                }
              />
            </label>
            <label className={ui.field}>
              From left (cm)
              <input
                className={`${ui.input} w-[84px]`}
                type="number"
                min={0}
                step={0.5}
                value={toCm(selectedProduct.x)}
                onChange={(e) =>
                  patchProduct(selectedShelf.id, selectedProduct.id, {
                    x: mm(Math.max(0, (Number(e.target.value) || 0) / 100)),
                  })
                }
              />
            </label>
            <div className={ui.field}>
              Colour
              <div className="flex gap-1 pt-1">
                {PRODUCT_COLORS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    aria-label={`Colour ${c}`}
                    className={`h-6 w-6 cursor-pointer rounded-md border-2 ${
                      selectedProduct.color === c ? "border-text" : "border-transparent"
                    }`}
                    style={{ background: c }}
                    onClick={() =>
                      patchProduct(selectedShelf.id, selectedProduct.id, { color: c })
                    }
                  />
                ))}
              </div>
            </div>
            <button type="button" className={ui.btnQuiet} onClick={duplicateProduct}>
              Duplicate
            </button>
            <button type="button" className={ui.btnDanger} onClick={removeSelected}>
              Remove
            </button>
          </div>
          <span className={`text-xs ${issueText ? "text-danger" : "text-muted"}`}>
            {issueText
              ? `Does not fit: ${issueText}.`
              : `${toCm(productWidth(selectedProduct))} cm of the front over ${selectedProduct.facings} ${
                  selectedProduct.facings === 1 ? "facing" : "facings"
                }, on the ${toCm(selectedShelf.elevation)} cm shelf.`}
          </span>
        </div>
      )}
    </div>
  );
}
